import React, { PropsWithChildren, useContext } from 'react';
import { MenuTopic } from '../types/topics';
import { useQuery } from 'react-query';
import { useFetch } from './FetchProvider';
import { useUserContext } from './UserContext';
import { useLanguageContext } from './LanguageProvider';

export type TopicsResponse = {
  primary: {
    groupName: string;
    navs: MenuTopic[];
  }[];

  footer: MenuTopic[];
};

export type TopicsContextProps = {
  topics: TopicsResponse;
  isLoading: boolean;
};

const TopicsContext = React.createContext<TopicsContextProps>({
  topics: { primary: [], footer: [] },
  isLoading: false,
});

/**
 * Topics provider.
 */
export const TopicsProvider = (
  props: PropsWithChildren<{
    topics?: { primary: []; footer: [] };
  }>,
) => {
  const { children, topics: initialTopics = { primary: [], footer: [] } } =
    props;
  const { get } = useFetch();
  const { user } = useUserContext();
  const { activeLanguage } = useLanguageContext();

  const { data: topics, isLoading } = useQuery(
    ['topic-library', activeLanguage],
    async ({ queryKey }) => {
      const response = await get<TopicsResponse>('/menu', {
        params: { language: queryKey[1] },
      });
      return response?.data;
    },
    {
      enabled: !!user,
    },
  );

  return (
    <TopicsContext.Provider
      value={{
        topics: topics || initialTopics,
        isLoading,
      }}
    >
      {children}
    </TopicsContext.Provider>
  );
};

/**
 * Hook to access the topics context.
 */
export const useTopicsContext = (): TopicsContextProps =>
  useContext(TopicsContext);
